import { useState } from 'react';

import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eluve/components';

import { CountBadge } from './CountBadge';

export type FilterType =
  | 'location'
  | 'specialty'
  | 'provider'
  | 'template'
  | 'microphone';

const TYPE_STYLES: Record<
  FilterType,
  { chip: string; dot: string; label: string; plural: string }
> = {
  location: {
    chip: 'bg-bg-warning-subtle border border-border-warning-subtle',
    dot: 'bg-bg-warning-strong-secondary',
    label: 'Location',
    plural: 'Locations',
  },
  specialty: {
    chip: 'bg-bg-positive-subtle border border-border-positive-subtle',
    dot: 'bg-bg-positive-strong-primary',
    label: 'Specialty',
    plural: 'Specialties',
  },
  provider: {
    chip: 'bg-bg-info-subtle border border-border-info-subtle',
    dot: 'bg-bg-info-strong-primary',
    label: 'User',
    plural: 'Users',
  },
  template: {
    chip: 'bg-bg-purple-subtle border border-border-primary',
    dot: 'bg-bg-purple-strong',
    label: 'Template Bundle',
    plural: 'Template Bundles',
  },
  microphone: {
    chip: 'bg-bg-gray-subtle border border-border-primary',
    dot: 'bg-bg-gray-strong',
    label: 'Microphone',
    plural: 'Microphones',
  },
};

const FilterChip: React.FC<{
  type: FilterType;
  label: string;
  onRemove: () => void;
}> = ({ type, label, onRemove }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div
        className={`flex items-center gap-1.5 rounded-full py-1 pr-2 pl-2.5 ${TYPE_STYLES[type].chip}`}
      >
        <Text size="sm" weight="semibold">
          {label}
        </Text>
        <button
          aria-label={`Remove ${label} filter`}
          onClick={onRemove}
          className="flex items-center rounded-full p-0.5 hover:bg-bg-secondary"
        >
          <Icon name="X" size="xs" />
        </button>
      </div>
    </TooltipTrigger>
    <TooltipContent>{TYPE_STYLES[type].label}</TooltipContent>
  </Tooltip>
);

export type FilterSelection = Record<FilterType, string[]>;

type NamedOption = { id: string; name: string };

/**
 * Compact toolbar button. The popover holds the applied filter chips
 * (OR within a type, AND across types) and a searchable picker with one
 * tab per dimension (the flat list outgrew a single scroll once
 * providers/templates/microphones joined); the trigger shows a count
 * badge when any filter is applied.
 */
export const FilterBar: React.FC<{
  locations: NamedOption[];
  specialties: string[];
  providers: NamedOption[];
  templates: NamedOption[];
  microphones: string[];
  selected: FilterSelection;
  onChange: (type: FilterType, values: string[]) => void;
}> = ({
  locations,
  specialties,
  providers,
  templates,
  microphones,
  selected,
  onChange,
}) => {
  const appliedCount = Object.values(selected).reduce(
    (sum, values) => sum + values.length,
    0,
  );
  // Value-list types (specialty/microphone) use the value itself as the id
  const groups: Array<{ type: FilterType; options: NamedOption[] }> = [
    { type: 'location', options: locations },
    {
      type: 'specialty',
      options: specialties.map((s) => ({ id: s, name: s })),
    },
    { type: 'provider', options: providers },
    { type: 'template', options: templates },
    {
      type: 'microphone',
      options: microphones.map((m) => ({ id: m, name: m })),
    },
  ];
  const optionName = (type: FilterType, id: string) =>
    groups
      .find((group) => group.type === type)!
      .options.find((option) => option.id === id)?.name ?? id;
  const [activeType, setActiveType] = useState<FilterType>('location');
  const activeOptions = groups
    .find((group) => group.type === activeType)!
    .options.filter((option) => !selected[activeType].includes(option.id));

  return (
    <TooltipProvider>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" aria-label="Filters">
            <Icon name="Filter" size="sm" />
            <Text size="sm" color="tertiary">
              Filters
            </Text>
            {appliedCount > 0 ? (
              <CountBadge count={appliedCount} />
            ) : (
              <Text size="sm" weight="semibold">
                All users
              </Text>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(92vw,44rem)] p-4">
          <Stack gap={3}>
            {appliedCount > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {groups.flatMap(({ type }) =>
                  selected[type].map((id) => (
                    <FilterChip
                      key={`${type}-${id}`}
                      type={type}
                      label={optionName(type, id)}
                      onRemove={() =>
                        onChange(
                          type,
                          selected[type].filter((entry) => entry !== id),
                        )
                      }
                    />
                  )),
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-1">
              {groups.map(({ type }) => (
                <button
                  key={type}
                  onClick={() => setActiveType(type)}
                  className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs ${
                    activeType === type
                      ? 'bg-bg-tertiary font-semibold'
                      : 'text-text-tertiary hover:bg-bg-secondary'
                  }`}
                >
                  <span
                    className={`size-1.5 rounded-full ${TYPE_STYLES[type].dot}`}
                  />
                  {TYPE_STYLES[type].plural}
                  {selected[type].length > 0 && (
                    <span className="font-semibold">
                      {selected[type].length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <Command
              key={activeType}
              className="rounded-lg border border-border-primary"
            >
              <CommandInput
                placeholder={`Search ${TYPE_STYLES[activeType].plural.toLowerCase()}…`}
              />
              {/* Scrolling lives on this wrapper — CommandList's built-in
                  no-scrollbar utility hides the native bar, and users need a
                  persistent one to see where they are in the long lists */}
              <div className="max-h-[55vh] overflow-y-scroll [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-bg-gray-strong [&::-webkit-scrollbar-track]:bg-transparent">
                <CommandList className="max-h-none py-1.5">
                  <CommandEmpty>No matches</CommandEmpty>
                  {activeOptions.map((option) => (
                    <CommandItem
                      key={option.id}
                      value={option.name}
                      onSelect={() =>
                        onChange(activeType, [
                          ...selected[activeType],
                          option.id,
                        ])
                      }
                    >
                      {option.name}
                    </CommandItem>
                  ))}
                </CommandList>
              </div>
            </Command>
          </Stack>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
